import { notification } from "antd";
import { useTranslate } from "@refinedev/core";
import { TDetailedStoryboard, Storyboard } from "pages/media/types";
import { useMediaAssetsStorage } from "hooks/useMediaAssetsStorage";
import { storyboardDetailedToStoryboard } from "../editor/helpers";
import { useCallback } from "react";

interface UseSaveStoryboardProps {
  projectId: string;
  organizationId: string;
  storyboardId?: string;
  storyboard?: Storyboard | TDetailedStoryboard;
  documentId?: string;
}

export const useSaveStoryboard = ({
  projectId,
  organizationId,
  storyboardId,
  storyboard,
  documentId,
}: UseSaveStoryboardProps) => {
  const t = useTranslate();
  const [api] = notification.useNotification();

  const { saveProjectAsset, isMutateLoading } = useMediaAssetsStorage({
    organizationId: organizationId,
    projectId: projectId,
  });

  // Resolves to whether the write actually landed. Deliberately not a
  // rejection: callers that only fire-and-forget this would turn one into an
  // unhandled rejection. Anything that acts on a save (marking the media out
  // of date, navigating on) must gate on the result (MED-7169).
  const saveStoryboard = useCallback(
    async (storyboardToSave?: Storyboard | TDetailedStoryboard) => {
      const storyboardData = storyboardToSave || storyboard;
      if (!documentId || !storyboardData || !storyboardId) {
        // we need a documentId to create the asset
        return false;
      }
      try {
        await saveProjectAsset(
          "Storyboard",
          storyboardDetailedToStoryboard(
            storyboardData as TDetailedStoryboard
          ),
          storyboardId,
          {
            document_id: documentId, // used only at creation of asset
          }
        );

        api.success({
          message: t("components.storyboard.StoryboardEditor.saveSuccessful"),
          description: t(
            "components.storyboard.StoryboardEditor.changesHaveBeen"
          ),
          placement: "bottomRight",
        });
        return true;
      } catch {
        api.error({
          message: t("components.storyboard.StoryboardEditor.saveFailed"),
          description: t("components.storyboard.StoryboardEditor.failedToSave"),
          placement: "bottomRight",
        });
        return false;
      }
    },
    [api, documentId, saveProjectAsset, storyboard, storyboardId, t]
  );

  return { saveStoryboard, isStoryboardSaving: isMutateLoading };
};
